import { post_SubmitGIWLApplication } from "services/online-application-service.openapi.zod";
import { z } from "zod";
import { toSingleLetterFromLowerCaseGender } from "utils/conversions";
import type { ApplicationState } from "store/application/applicationSlice";
import {
    validateSubmissionState,
    buildSecondaryAddressee,
    buildApplicantIdentity,
    buildPrimaryBeneficiary,
} from "mappers/shared/submissionMapper.base";

const BodySchema = post_SubmitGIWLApplication.parameters.shape.body;
type GiwlSubmissionRequestBody = z.infer<typeof BodySchema>;

/**
 * Transforms GIWL Redux state into the format required for submission API
 * @param giwlState - The application state from Redux store
 * @returns Formatted submission request body
 */
export function transformGIWLSubmissionData(giwlState: ApplicationState): GiwlSubmissionRequestBody {
    const {
        quoteData,
        contactData,
        paymentInformationData,
        contactInformation,
        policyBeneficiary,
        secondaryAddressee,
        landingPageData,
    } = validateSubmissionState(giwlState);

    const additionalData = [
        { key: "insurance_type", value: "GIWL" },
        { key: "selectedPlan", value: "GW22" },
        { key: "selectedTerm", value: "10" },
        { key: "policy_type", value: quoteData.policyType ?? "No" },
    ];

    // Legacy parity: the campaign code doubles as the applicant key code. parseGIWLCampaignParams
    // already defaults it to the mail keycode when no campaign params are present, so landingPageData
    // always carries a value by submission time (see useGIWLQuoteHydration).
    const campaignCode = landingPageData?.campaign.campaignCode!;

    return {
        // policyNumber is required: it is minted up front via /generateFastPolicyNumber during
        // contact submission and carried on contactData. applicationID remains backend-internal
        // and is omitted (optional on the request schema).
        policyNumber: contactData.policyNumber!,
        faceAmount: quoteData.selectedCoverageAmount!,
        modalPremiumAmount: quoteData.selectedCoveragePremium!,
        signedTimestamp: new Date().toISOString(),
        applicationSubmissionTimestamp: new Date().toISOString(),
        paymentToken: paymentInformationData.paymentToken,
        paymentType: paymentInformationData.paymentType,
        lastFourDigits: paymentInformationData.lastFourDigits!,

        applicant: {
            ...buildApplicantIdentity(contactInformation, quoteData.zipCode!), // MLTA: zipCode comes from quoteData (collected on quote page)
            aaaMember: true,
            aaaMemberNumber: contactInformation.aaaMemberNumber,
            gender: quoteData.gender ? toSingleLetterFromLowerCaseGender(quoteData.gender)! : "M",
            keyCode: campaignCode,
        },

        beneficiaries: [buildPrimaryBeneficiary(policyBeneficiary)],

        ...buildSecondaryAddressee(secondaryAddressee),
        replacementIndicator: contactData.coverageReplacement?.replacementIndicator ?? false,

        campaign: {
            campaignType: "CAMPAIGNTYPE_GUARANTEED_ISSUE_WHOLE_LIFE",
            productCode: "GIWL",
            clubCode: landingPageData?.campaign.clubCode ?? contactData.clubCode!,
            leadSource: landingPageData?.campaign.leadSource!,
            campaignName: "GWL",
            campaignCode,
        },

        additionalData,
    };
}
